import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Button, Col, Form, Input, Popconfirm, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateAgentBranding,
  useDeleteAgentBranding,
  useGetAgentBranding,
  useUpdateAgentBranding,
} from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import { ACCESS_TREE_PATH_DELIMITER, rowGutter } from "../../../../common/constants";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { AgentType } from "../../enums";
import { Agent, CreateUpdateAgentBranding } from "../../types";
import { isLegalAgent } from "../../utils";

const colSpan = 6;

interface Props {
  agent: Agent;
}

const AgentBrandingForm = ({ agent }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateAgentBranding>();

  const { data: branding } = useGetAgentBranding(agent.id);
  const createAgentBranding = useCreateAgentBranding();
  const updateAgentBranding = useUpdateAgentBranding();
  const deleteAgentBranding = useDeleteAgentBranding();

  useEffect(() => {
    if (branding) {
      const { officialRepresentative, commissionsPayer, createdAt, updatedAt, id, ...formData } = branding;

      form.setFieldsValue({
        ...formData,
        officialRepresentativeId: officialRepresentative?.id,
        commissionsPayerId: commissionsPayer?.id,
      });
    } else {
      form.resetFields();
    }
  }, [branding, form]);

  const handleFormFinish = (values: CreateUpdateAgentBranding) => {
    if (branding) {
      updateAgentBranding.mutate(
        { id1: agent.id, id2: branding.id, object: values },
        { onError: (error) => setErrorsToForm(form, "agent.branding.attrs", error.violations) },
      );
    } else {
      createAgentBranding.mutate(
        { id: agent.id, object: values },
        { onError: (error) => setErrorsToForm(form, "agent.branding.attrs", error.violations) },
      );
    }
  };

  return (
    <>
      <ItemCreatedUpdatedInfoView item={branding} className="margin-bottom-medium" />
      <Form form={form} layout="vertical" name="agentBrandingForm" onFinish={handleFormFinish}>
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item
              name="logoResourcePath"
              label={t("agent.branding.attrs.logoResourcePath")}
              rules={[validations.min(5)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="primaryColor"
              label={t("agent.branding.attrs.primaryColor")}
              rules={[validations.pattern(regexPatterns.colorRegex)]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={colSpan}>
            <Form.Item
              name="secondaryColor"
              label={t("agent.branding.attrs.secondaryColor")}
              rules={[validations.pattern(regexPatterns.colorRegex)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item
              name="emailSignatureTemplatePath"
              label={t("agent.branding.attrs.emailSignatureTemplatePath")}
              rules={[validations.min(6)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="officialRepresentativeId" label={t("agent.branding.attrs.officialRepresentativeId")}>
              <AgentSelect
                optionsProps={{
                  allowedIds: isLegalAgent(agent)
                    ? agent.representatives.map((representative) => representative.id)
                    : undefined,
                  forceRender: true,
                }}
                allowClear
                disabled={agent.type !== AgentType.LEGAL}
              />
            </Form.Item>
          </Col>
          <Col span={colSpan}>
            <Form.Item name="commissionsPayerId" label={t("agent.branding.attrs.commissionsPayerId")}>
              <AgentSelect allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item
              name="officialRepresentativeSignatureResourcePath"
              label={t("agent.branding.attrs.officialRepresentativeSignatureResourcePath")}
              rules={[validations.min(5)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <div className="margin-top-medium">
          <Button className="margin-right-tiny" type="primary" htmlType="submit" icon={<AntIcon type="save" />}>
            {t(branding ? "common.save" : "common.create")}
          </Button>

          {branding && (
            <Popconfirm
              title={t("agent.branding.helpers.deleteBrandingConfirm", { aggregatedName: agent.aggregatedName })}
              icon={<PopconfirmDeleteIcon />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="danger"
              disabled={!agent.accessTreePath?.includes(ACCESS_TREE_PATH_DELIMITER)}
              onConfirm={() => deleteAgentBranding.mutate({ id1: agent.id, id2: branding.id })}
            >
              <Button
                icon={<AntIcon type="delete" />}
                danger
                disabled={!agent.accessTreePath?.includes(ACCESS_TREE_PATH_DELIMITER)}
              >
                {t("common.delete")}
              </Button>
            </Popconfirm>
          )}
        </div>
      </Form>
    </>
  );
};

export default AgentBrandingForm;
